import type { Theme } from "@mui/material/styles"

import { getSubtleEdgeShadow, getSubtleSurfaceShadow } from "components/common/elevation"

export const DATA_VIEW_HEADER_HEIGHT = 48
export const DATA_VIEW_CALENDAR_MIN_WIDTH = 700
export const DATA_VIEW_CALENDAR_ROW_HEIGHT = 192

export const DATA_VIEW_TABLE_BODY_DIVIDER_STYLES = {
  "& .MuiTableCell-root": {
    borderBottom: 1,
    borderColor: "divider",
  },
  "& > .MuiTableRow-root:last-child > .MuiTableCell-root": {
    borderBottom: 0,
  },
} as const

export const getDataViewHeaderStyles = (theme: Theme) => ({
  backgroundColor: theme.palette.action.hover,
  borderBottom: `${theme.spacing(0.125)} solid ${theme.palette.divider}`,
  boxShadow: getSubtleSurfaceShadow(theme),
})

export const getDataViewHorizontalScrollbarStyles = (theme: Theme) => ({
  "&::-webkit-scrollbar-thumb:horizontal": {
    backgroundColor: theme.palette.action.active,
    border: `${theme.spacing(0.25)} solid ${theme.palette.background.paper}`,
    borderRadius: theme.shape.borderRadius * 2,
  },
  "&::-webkit-scrollbar-track:horizontal": {
    backgroundColor: theme.palette.action.selected,
    borderTop: `${theme.spacing(0.125)} solid ${theme.palette.divider}`,
  },
  "&::-webkit-scrollbar:horizontal": {
    backgroundColor: theme.palette.action.selected,
    height: theme.spacing(1.5),
    WebkitAppearance: "none",
  },
})

export const getDataViewStickyColumnStyles = (theme: Theme, edge: "left" | "right") => {
  const edgeBorder = `${theme.spacing(0.125)} solid ${theme.palette.divider}`

  return {
    backgroundColor: theme.palette.background.paper,
    borderLeft: edge === "right" ? edgeBorder : undefined,
    borderRight: edge === "left" ? edgeBorder : undefined,
    boxShadow: getSubtleEdgeShadow(theme, edge),
  }
}

type DataViewTableStylesOptions = {
  gridTemplateColumns: string
  minWidth: number | string
}

export const getDataViewTableStyles = (
  theme: Theme,
  { gridTemplateColumns, minWidth }: DataViewTableStylesOptions,
) => ({
  "& .MuiTableBody-root": {
    ...getDataViewHorizontalScrollbarStyles(theme),
    ...DATA_VIEW_TABLE_BODY_DIVIDER_STYLES,
    display: "block",
    isolation: "isolate",
    minHeight: 0,
    overflowX: "scroll",
    overflowY: "auto",
    overscrollBehavior: "contain",
    scrollbarGutter: "stable",
  },
  "& .MuiTableHead-root": {
    ...getDataViewHeaderStyles(theme),
    display: "block",
    isolation: "isolate",
    overflowX: "hidden",
    overflowY: "auto",
    position: "relative",
    scrollbarGutter: "stable",
    zIndex: 2,
  },
  "& .MuiTableHead-root .MuiTableCell-root": {
    ...theme.typography.subtitle2,
    alignItems: "center",
    borderBottom: 0,
    color: theme.palette.text.secondary,
    display: "flex",
    justifyContent: "center",
    paddingBottom: 0,
    paddingTop: 0,
  },
  "& .MuiTableHead-root .MuiTableCell-root.MuiTableCell-alignLeft": {
    justifyContent: "flex-start",
  },
  "& .MuiTableHead-root .MuiTableCell-root.MuiTableCell-alignRight": {
    justifyContent: "flex-end",
  },
  "& .MuiTableHead-root .MuiTableRow-root": {
    height: DATA_VIEW_HEADER_HEIGHT,
  },
  "& .MuiTableRow-root": {
    display: "grid",
    gridTemplateColumns,
    minWidth,
  },
  display: "grid",
  gridTemplateRows: "auto minmax(0, 1fr)",
  height: "100%",
  width: "100%",
})
